import { ArrowRight } from "lucide-react";
import { Reveal, SectionTag, StatusPill } from "./Reveal";

const CHAPTERS = [
  {
    num: "01",
    title: "Level 1 — Real-Time Videochat MVP",
    text: "Live photorealistic character, natural voice, facial response and real-time video.",
    status: "In development",
    tone: "amber",
    path: "/roadmap/real-time-videochat-mvp",
    linkLabel: "How we're building it",
  },
  {
    num: "02",
    title: "Level 2 — Richer Interaction",
    text: "User-triggered actions, reactions and richer interaction.",
    status: "Next",
    tone: "white",
    path: "/roadmap/level-2-richer-interaction",
    linkLabel: "See what's next",
  },
  {
    num: "03",
    title: "Level 3 — Advanced Interaction",
    text: "Advanced movement, environment interaction and premium formats.",
    status: "Future",
    tone: "white",
    path: "/roadmap/level-3-advanced-interaction",
    linkLabel: "See the future stage",
  },
  {
    num: "04",
    title: "Commercial Platform",
    text: "Validation, paid usage, controlled acquisition and the commercial platform.",
    status: "Future",
    tone: "white",
    path: "/roadmap/commercial-platform",
    linkLabel: "How we're building it",
  },
  {
    num: "05",
    title: "Humanoid AI",
    text: "Extend the AI characters and intelligence into physical humanoid robots.",
    status: "Long-term vision",
    tone: "white",
    path: "/roadmap/humanoid-ai",
    linkLabel: "Explore the long-term vision",
  },
];

const PROGRESSION = ["Level 1 MVP", "Level 2", "Level 3", "Commercial Platform", "Humanoid AI"];

export default function Roadmap() {
  return (
    <section id="roadmap" data-testid="roadmap-section" className="relative py-16 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>Roadmap</SectionTag>
          <h2 className="font-display mt-5 max-w-2xl text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            From AI Characters to <span className="text-cyan-300">Humanoid AI</span>
          </h2>
        </Reveal>

        <div className="mt-16 border-t border-white/10">
          {CHAPTERS.map(({ num, title, text, status, tone, path, linkLabel }, i) => (
            <Reveal key={num} delay={0.05 * i}>
              <div
                data-testid={`roadmap-chapter-${num}`}
                className="group grid items-center gap-4 border-b border-white/10 py-10 transition-colors duration-500 hover:bg-white/[0.02] md:grid-cols-[120px_1fr_auto] md:gap-10 md:px-4"
              >
                <span className="font-display text-6xl font-extralight tracking-tighter text-white/10 transition-colors duration-500 group-hover:text-cyan-300/30 lg:text-7xl">
                  {num}
                </span>
                <div>
                  <h3 className="font-display text-xl font-medium uppercase tracking-[0.12em] text-white md:text-2xl">
                    {title}
                  </h3>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#C5CBD5]">{text}</p>
                  {path && (
                    <a
                      href={path}
                      data-testid={`roadmap-link-${num}`}
                      className="mt-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/90 transition-colors duration-300 hover:text-cyan-100"
                    >
                      {linkLabel}
                      <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  )}
                </div>
                <StatusPill tone={tone}>{status}</StatusPill>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.12}>
          <p data-testid="roadmap-seis-note" className="mt-10 max-w-3xl text-xs leading-relaxed text-white/70">
            SEIS capital is allocated exclusively to the AI video companion platform and directly
            related product development. Humanoid AI is a separate long-term vision and is not part
            of the current round.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="roadmap-progression" className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-3">
            {PROGRESSION.map((step, i) => (
              <div key={step} className="flex items-center gap-4">
                <span className={`font-display text-sm tracking-wide ${i === PROGRESSION.length - 1 ? "text-cyan-300" : "text-white/70"}`}>
                  {step}
                </span>
                {i < PROGRESSION.length - 1 && <ArrowRight size={14} className="text-white/25" />}
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
